"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  useRealtimeChannel,
  type RealtimeOrigin,
} from "./use-realtime-channel";

export interface ReviewAutomationSnapshot {
  dueCount: number;
  upcomingCount: number;
  lastChecked: string;
  updatedAt: string;
  snoozedUntil?: string | null;
  lastPromptedAt?: string | null;
  lastSessionAt?: string | null;
}

export interface UseReviewAutomationOptions {
  userId?: string | null;
  enabled?: boolean;
  fetchOnMount?: boolean;
}

interface ReviewStatsSyncMessage extends ReviewAutomationSnapshot {
  userId: string;
  origin?: RealtimeOrigin | "api";
  clientId?: string;
}

const STORAGE_KEY = "talkmaster-review-automation";
const CHANNEL_NAME = "talkmaster:review-stats";
const SOCKET_EVENT = "review:stats-updated";
const DEFAULT_FETCH_INTERVAL_MS = 6 * 60 * 60 * 1000; // 6h
const PROMPT_COOLDOWN_MS = 2 * 60 * 60 * 1000; // 2h
const DEFAULT_SNOOZE_HOURS = 6;
const DISMISS_HOURS = 24;

const nowIso = () => new Date().toISOString();

export function useReviewAutomation({
  userId,
  enabled = true,
  fetchOnMount = true,
}: UseReviewAutomationOptions) {
  const [snapshot, setSnapshot] = useState<ReviewAutomationSnapshot | null>(
    null,
  );
  const snapshotRef = useRef<ReviewAutomationSnapshot | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const lastFetchRef = useRef<number>(0);

  const persistSnapshot = useCallback(
    (next: ReviewAutomationSnapshot | null) => {
      if (typeof window === "undefined") return;
      if (!next) {
        window.localStorage.removeItem(STORAGE_KEY);
      } else {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      }
    },
    [],
  );

  const applySnapshot = useCallback(
    (
      updater:
        | ReviewAutomationSnapshot
        | ((
            prev: ReviewAutomationSnapshot | null,
          ) => ReviewAutomationSnapshot | null),
    ) => {
      setSnapshot((prev) => {
        const next =
          typeof updater === "function"
            ? (
                updater as (
                  prev: ReviewAutomationSnapshot | null,
                ) => ReviewAutomationSnapshot | null
              )(prev)
            : updater;
        persistSnapshot(next);
        return next;
      });
    },
    [persistSnapshot],
  );

  const handleIncoming = useCallback(
    (payload: ReviewStatsSyncMessage) => {
      if (!payload) return;
      applySnapshot((prev) => {
        const base: ReviewAutomationSnapshot = {
          dueCount: payload.dueCount ?? prev?.dueCount ?? 0,
          upcomingCount: payload.upcomingCount ?? prev?.upcomingCount ?? 0,
          lastChecked: payload.lastChecked ?? prev?.lastChecked ?? nowIso(),
          updatedAt: payload.updatedAt ?? nowIso(),
          snoozedUntil: prev?.snoozedUntil ?? null,
          lastPromptedAt: prev?.lastPromptedAt ?? null,
          lastSessionAt: prev?.lastSessionAt ?? null,
        };
        return base;
      });
    },
    [applySnapshot],
  );

  // Load persisted snapshot
  useEffect(() => {
    if (typeof window === "undefined") return;
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (!stored) return;
    try {
      const parsed = JSON.parse(stored);
      if (typeof parsed?.dueCount === "number") {
        setSnapshot({
          dueCount: Number(parsed.dueCount) || 0,
          upcomingCount: Number(parsed.upcomingCount ?? 0) || 0,
          lastChecked: parsed.lastChecked ?? nowIso(),
          updatedAt: parsed.updatedAt ?? nowIso(),
          snoozedUntil: parsed.snoozedUntil ?? null,
          lastPromptedAt: parsed.lastPromptedAt ?? null,
          lastSessionAt: parsed.lastSessionAt ?? null,
        });
      }
    } catch {
      // ignore parse errors
    }
  }, []);

  const { publishLocal } = useRealtimeChannel<ReviewStatsSyncMessage>({
    userId,
    enabled,
    channelName: `${CHANNEL_NAME}:${userId ?? "global"}`,
    socketEvent: SOCKET_EVENT,
    onMessage: (payload) => {
      if (payload.origin === "local") return; // already applied during publish
      handleIncoming(payload);
    },
  });

  const broadcastLocalSnapshot = useCallback(
    (next: ReviewAutomationSnapshot) => {
      if (!userId) return;
      publishLocal({
        userId,
        ...next,
      });
    },
    [publishLocal, userId],
  );

  useEffect(() => {
    snapshotRef.current = snapshot;
  }, [snapshot]);

  const refresh = useCallback(async () => {
    if (!enabled || !userId) return;
    const now = Date.now();
    if (now - lastFetchRef.current < 30_000) {
      return;
    }
    lastFetchRef.current = now;
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(
        "/api/vocabulary/review?includeUpcoming=true&limit=1",
        {
          credentials: "include",
          headers: {
            Accept: "application/json",
          },
        },
      );
      if (!response.ok) {
        throw new Error("failed_to_fetch");
      }
      const data = await response.json();
      const dueCount = Number(data?.meta?.totalDue ?? 0) || 0;
      const upcomingCount = Number(data?.meta?.totalUpcoming ?? 0) || 0;
      const currentSnapshot = snapshotRef.current;
      const next: ReviewAutomationSnapshot = {
        dueCount,
        upcomingCount,
        lastChecked: nowIso(),
        updatedAt: nowIso(),
        snoozedUntil: currentSnapshot?.snoozedUntil ?? null,
        lastPromptedAt: currentSnapshot?.lastPromptedAt ?? null,
        lastSessionAt: currentSnapshot?.lastSessionAt ?? null,
      };
      applySnapshot(next);
      broadcastLocalSnapshot(next);
    } catch (err) {
      setError(err instanceof Error ? err.message : "unknown_error");
    } finally {
      setLoading(false);
    }
  }, [applySnapshot, broadcastLocalSnapshot, enabled, userId]);

  // Auto refresh by interval
  useEffect(() => {
    if (!enabled || !userId || !fetchOnMount) return;
    void refresh();
    const timer = setInterval(() => {
      void refresh();
    }, DEFAULT_FETCH_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [enabled, fetchOnMount, refresh, userId]);

  const persistPromptAction = useCallback(
    async (payload: { action: "snooze" | "dismiss"; hours?: number }) => {
      if (!userId) return;
      try {
        await fetch("/api/vocabulary/review/prompt", {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
      } catch (error) {
        console.error("Failed to persist review prompt action", error);
      }
    },
    [userId],
  );

  const snooze = useCallback(
    (hours: number = DEFAULT_SNOOZE_HOURS) => {
      const until = new Date();
      until.setHours(until.getHours() + hours);
      const nextSnapshot = {
        dueCount: snapshot?.dueCount ?? 0,
        upcomingCount: snapshot?.upcomingCount ?? 0,
        lastChecked: nowIso(),
        updatedAt: nowIso(),
        snoozedUntil: until.toISOString(),
        lastPromptedAt: nowIso(),
        lastSessionAt: snapshot?.lastSessionAt ?? null,
      };
      applySnapshot((prev) => {
        if (!prev) return nextSnapshot;
        return {
          ...prev,
          snoozedUntil: nextSnapshot.snoozedUntil,
          lastPromptedAt: nextSnapshot.lastPromptedAt,
          updatedAt: nextSnapshot.updatedAt,
        };
      });
      broadcastLocalSnapshot(nextSnapshot);
      void persistPromptAction({ action: "snooze", hours });
    },
    [
      applySnapshot,
      broadcastLocalSnapshot,
      persistPromptAction,
      snapshot?.dueCount,
      snapshot?.lastSessionAt,
      snapshot?.upcomingCount,
    ],
  );

  const dismiss = useCallback(() => {
    snooze(DISMISS_HOURS);
    void persistPromptAction({ action: "dismiss", hours: DISMISS_HOURS });
  }, [persistPromptAction, snooze]);

  const markSessionLaunched = useCallback(() => {
    const timestamp = nowIso();
    const sessionSnapshot: ReviewAutomationSnapshot = {
      dueCount: snapshot?.dueCount ?? 0,
      upcomingCount: snapshot?.upcomingCount ?? 0,
      lastChecked: timestamp,
      updatedAt: timestamp,
      snoozedUntil: null,
      lastPromptedAt: snapshot?.lastPromptedAt ?? null,
      lastSessionAt: timestamp,
    };

    applySnapshot((prev) => {
      if (!prev) {
        return sessionSnapshot;
      }
      return {
        ...prev,
        lastSessionAt: timestamp,
        snoozedUntil: null,
        lastChecked: timestamp,
        updatedAt: timestamp,
      };
    });
    broadcastLocalSnapshot(sessionSnapshot);
    if (userId) {
      void fetch("/api/vocabulary/review/prompt", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "session" }),
      }).catch((error) =>
        console.error("Failed to persist review session launch", error),
      );
    }
  }, [
    applySnapshot,
    broadcastLocalSnapshot,
    snapshot?.dueCount,
    snapshot?.lastPromptedAt,
    snapshot?.upcomingCount,
    userId,
  ]);

  const promptReady = useMemo(() => {
    if (!snapshot) return false;
    const now = Date.now();
    if (snapshot.snoozedUntil) {
      const snoozeUntil = new Date(snapshot.snoozedUntil).getTime();
      if (snoozeUntil > now) return false;
    }
    if (snapshot.lastPromptedAt) {
      const lastPrompt = new Date(snapshot.lastPromptedAt).getTime();
      if (now - lastPrompt < PROMPT_COOLDOWN_MS) return false;
    }
    if (snapshot.dueCount >= 3) return true;
    if (snapshot.dueCount === 0 && snapshot.upcomingCount >= 5) return true;
    return false;
  }, [snapshot]);

  return {
    stats: snapshot,
    loading,
    error,
    refresh,
    promptReady,
    snooze,
    dismiss,
    markSessionLaunched,
  };
}
